import { ViewerQuery, ViewerDocument } from "graphql/queries/Viewer.generated";
import { useApolloClient } from "@apollo/client";
import { Teacher, Student } from "graphql/globalTypes.generated";

// convenient hook to get the viewer in logged in app
export function useCurrentUser() {
  const client = useApolloClient();
  const data = client.readQuery<ViewerQuery>({
    query: ViewerDocument,
  });

  if (!data) {
    throw new Error("Cannot access viewer data when user is not logged in");
  }

  return data.viewer.currentUser;
}

export function useCurrentUserNoExcept() {
  try {
    return useCurrentUser();
  } catch {
    // swallow exception
  }
  return null;
}

export function useTeacherCurrentUser() {
  const teacher = useCurrentUser();
  if (teacher.__typename !== "Teacher") {
    throw new Error(
      "Invariant Violation: Using teacher viewer with student user.",
    );
  }
  return teacher;
}

export function useStudentCurrentUser() {
  const student = useCurrentUser();
  if (student.__typename !== "Student") {
    throw new Error(
      "Invariant Violation: Using student viewer with teacher user.",
    );
  }
  return student;
}

export function useUserActivityState() {
  const currentUser = useCurrentUserNoExcept();
  if (!currentUser) return undefined;
  return currentUser.isActive ? "active" : "archived";
}

// this hook assumes that a student can only be connected to one school.
// it can be used for segment events and split tests where we need to
// get the school ID from the current user (teacher or student),
// but for other cases, the school ID should be accessed from the user object directly.
export function useCurrentSchoolId() {
  const user = useCurrentUserNoExcept();
  if (!user) {
    return "";
  }

  if (user.__typename === "Teacher") {
    // we can have teachers that are not connected to a school
    const { school } = user as Teacher;
    return school ? school.id : "";
  }

  if (user.__typename === "Student") {
    const { schools } = user as Student;
    if (schools?.length) {
      // technically, students can be connected to multiple teachers through connections,
      // and they can be connected to multiple schools, but we have an application-level
      // constraints that prevents that for now
      return schools[0].id;
    }
  }

  return "";
}
